import { apiClient } from './client';
import type {
  EtsngImportInput,
  GngImportInput,
  LinkCandidateDto,
  LinkFrom,
  LinkImportInput,
  ReferenceBookId,
  ReferenceBookMetaDto,
  ReferenceCodeDto,
  ReferenceListDto,
  SimpleClassifierId,
  SimpleClassifierImportInput,
} from '@expeditor/shared';

/** Справочники НСИ: ГНГ и ЕТСНГ. */
export const referenceDataApi = {
  /** Поиск по коду или наименованию в справочнике. */
  async search(book: ReferenceBookId, q: string, limit = 20): Promise<ReferenceCodeDto[]> {
    const { data } = await apiClient.get<ReferenceCodeDto[]>(`/reference-data/${book}/search`, {
      params: { q, limit },
    });
    return data;
  },
  /** Одна строка по коду (для автоподстановки, в т.ч. признака опасности ЕТСНГ). */
  async byCode(book: ReferenceBookId, code: string): Promise<ReferenceCodeDto | null> {
    const { data } = await apiClient.get<ReferenceCodeDto | null>(
      `/reference-data/${book}/by-code/${encodeURIComponent(code)}`,
    );
    return data;
  },
  /** Метаданные справочника (только ADMIN). */
  async meta(book: ReferenceBookId): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.get<ReferenceBookMetaDto>(`/admin/reference-data/${book}/meta`);
    return data;
  },
  /** Импорт пачки строк ГНГ (только ADMIN). */
  async importGng(input: GngImportInput): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.post<ReferenceBookMetaDto>(
      '/admin/reference-data/gng/import',
      input,
    );
    return data;
  },
  /** Импорт пачки строк ЕТСНГ (только ADMIN). */
  async importEtsng(input: EtsngImportInput): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.post<ReferenceBookMetaDto>(
      '/admin/reference-data/etsng/import',
      input,
    );
    return data;
  },
  /** Кандидаты встречной номенклатуры по переходнику (from = gng | etsng). */
  async link(from: LinkFrom, code: string): Promise<LinkCandidateDto[]> {
    const { data } = await apiClient.get<LinkCandidateDto[]>(
      `/reference-data/link/${from}/${encodeURIComponent(code)}`,
    );
    return data;
  },
  /** Метаданные переходника (только ADMIN). */
  async linkMeta(): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.get<ReferenceBookMetaDto>('/admin/reference-data/link/meta');
    return data;
  },
  /** Импорт пачки переходника (только ADMIN). */
  async importLink(input: LinkImportInput): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.post<ReferenceBookMetaDto>(
      '/admin/reference-data/link/import',
      input,
    );
    return data;
  },
  /** Страница справочника с общим числом строк (админ-просмотр). */
  async list(
    book: ReferenceBookId,
    q: string,
    page: number,
    pageSize: number,
  ): Promise<ReferenceListDto> {
    const { data } = await apiClient.get<ReferenceListDto>(`/reference-data/${book}/list`, {
      params: { q, page, pageSize },
    });
    return data;
  },
  /** Страница классификатора с общим числом строк (админ-просмотр). */
  async classifierList(
    id: SimpleClassifierId,
    q: string,
    page: number,
    pageSize: number,
  ): Promise<ReferenceListDto> {
    const { data } = await apiClient.get<ReferenceListDto>(
      `/reference-data/classifier/${id}/list`,
      { params: { q, page, pageSize } },
    );
    return data;
  },
  /** Поиск по простому классификатору (страны/упаковка/условия поставки). */
  async classifierSearch(
    id: SimpleClassifierId,
    q: string,
    limit = 20,
  ): Promise<ReferenceCodeDto[]> {
    const { data } = await apiClient.get<ReferenceCodeDto[]>(
      `/reference-data/classifier/${id}/search`,
      { params: { q, limit } },
    );
    return data;
  },
  /** Строка классификатора по коду. */
  async classifierByCode(id: SimpleClassifierId, code: string): Promise<ReferenceCodeDto | null> {
    const { data } = await apiClient.get<ReferenceCodeDto | null>(
      `/reference-data/classifier/${id}/by-code/${encodeURIComponent(code)}`,
    );
    return data;
  },
  /** Метаданные классификатора (только ADMIN). */
  async classifierMeta(id: SimpleClassifierId): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.get<ReferenceBookMetaDto>(
      `/admin/reference-data/classifier/${id}/meta`,
    );
    return data;
  },
  /** Импорт классификатора (только ADMIN). */
  async classifierImport(
    id: SimpleClassifierId,
    input: SimpleClassifierImportInput,
  ): Promise<ReferenceBookMetaDto> {
    const { data } = await apiClient.post<ReferenceBookMetaDto>(
      `/admin/reference-data/classifier/${id}/import`,
      input,
    );
    return data;
  },
};
